import Cookies from "universal-cookie";
import { hasValidToken } from "./authSession";

const ENVIRONMENT = process.env.VITE_ENV || process.env.NODE_ENV;
const API_BASE_URL = (process.env.VITE_API_URL || process.env.VITE_NODE_API_URL || "http://localhost:3000").replace(/\/+$/, "");
const TELEMETRY_ENDPOINT = `${API_BASE_URL}/telemetry/frontend`;
const RELEASE = process.env.VITE_RELEASE || null;
const ENABLED = ENVIRONMENT === "production";
const ALLOWED_EVENTS = new Set([
    "frontend.api.failure",
    "frontend.api.permission_denied",
    "frontend.auth.failure",
    "frontend.chunk_load_failed",
    "frontend.bootstrap.failure",
    "frontend.app.crash",
    "frontend.unhandled_error",
    "frontend.unhandled_rejection",
    "frontend.sse.disconnected",
    "frontend.workflow.failure",
    "frontend.report.failure",
    "frontend.export.failure",
    "frontend.performance.slow_view",
]);
const recentEvents = new Map();
const DEDUPE_WINDOW_MS = 30_000;
const cookies = new Cookies();

function truncate(value, max = 300) {
    if (value === undefined || value === null) {
        return null;
    }

    const text = String(value).trim();
    if (!text) {
        return null;
    }

    return text.length > max ? `${text.slice(0, max)}…` : text;
}

function normalizeUrl(value) {
    const text = truncate(value, 500);
    if (!text) {
        return null;
    }

    try {
        const parsed = new URL(text, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
    } catch (_error) {
        return text;
    }
}

function getRoute() {
    if (typeof window === "undefined") {
        return null;
    }

    return `${window.location.pathname || ""}${window.location.search || ""}`;
}

function buildHeaders() {
    const headers = {
        "Content-Type": "application/json",
    };

    let token = cookies.get("tokenR");
    if (hasValidToken(token)) {
        if (!token.startsWith("Bearer ")) {
            token = `Bearer ${token}`;
        }
        headers.Authorization = token;
    }

    return headers;
}

function shouldSkip(eventName, payload) {
    if (!ENABLED || typeof window === "undefined") {
        return true;
    }

    if (!ALLOWED_EVENTS.has(eventName)) {
        return true;
    }

    const dedupeKey = JSON.stringify([
        eventName,
        payload.route,
        payload.url,
        payload.statusCode,
        payload.errorCode,
        payload.message,
    ]);
    const now = Date.now();
    const previous = recentEvents.get(dedupeKey);

    if (previous && now - previous < DEDUPE_WINDOW_MS) {
        return true;
    }

    recentEvents.set(dedupeKey, now);
    return false;
}

export function emitFrontendTelemetry(eventName, payload = {}) {
    if (typeof window !== "undefined" && /^\/pre-joining\/?$/.test(window.location.pathname)) return Promise.resolve();
    const body = {
        event: eventName,
        area: truncate(payload.area, 80),
        action: truncate(payload.action, 80),
        route: normalizeUrl(payload.route || getRoute()),
        url: normalizeUrl(payload.url),
        method: truncate(payload.method, 16)?.toUpperCase() || null,
        statusCode: Number.isInteger(payload.statusCode) ? payload.statusCode : null,
        requestId: truncate(payload.requestId, 120),
        errorCode: truncate(payload.errorCode, 80),
        message: truncate(payload.message, 500),
        durationMs: typeof payload.durationMs === "number" ? Number(payload.durationMs.toFixed(1)) : null,
        retryCount: Number.isInteger(payload.retryCount) ? payload.retryCount : null,
        release: RELEASE,
        source: "browser",
    };

    if (shouldSkip(eventName, body)) {
        return Promise.resolve(false);
    }

    return fetch(TELEMETRY_ENDPOINT, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: buildHeaders(),
        body: JSON.stringify(body),
    }).then(() => true).catch(() => false);
}

export default emitFrontendTelemetry;
